import ApiService from './api.service'

class BirthdayError extends Error {
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const BirthdayService = {

    /**
     * Fetch birthday groups for the current page. 
     * 
     * @returns birthdayGroupList
     * @throws BirthdayError 
    **/
    fetchGroups: async function(page) {
        try {
            const response = await ApiService.get("/birthdays/groups?page="+page)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Create new birthday group
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    createGroup: async function(data)
    {
        try {
            const response = await ApiService.post("/birthdays/groups", data)
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Update birthday group
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    updateGroup: async function(data, id)
    {
        try {
            const response = await ApiService.put(`/birthdays/groups/${id}`, data)
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch birthday group by id. 
     * 
     * @returns Birthday
     * @throws BirthdayError 
    **/
    fetchGroup: async function(id) {
        try {
            const response = await ApiService.get(`/birthdays/groups/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete birthday group by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteGroup: async function(id) {
        try {
            const response = await ApiService.delete(`/birthdays/groups/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi birthday groups by ids. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteMultipleGroups: async function(ids) {
        try {
            const response = await ApiService.delete("/birthdays/groups", {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Add contact to birthday group 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    addContact: async function(groupId, data) {
        try {
            const response = await ApiService.post(`/birthdays/groups/${groupId}/contacts`, data)
            
            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Add contacts to birthday group 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    addContactBulk: async function(groupId, data) {
        try {
            const response = await ApiService.post(`/birthdays/groups/${groupId}/contacts/bulk`, data)
            
            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch birthday group's contact by id. 
     * 
     * @returns BirthdayContact
     * @throws BirthdayError 
    **/
    fetchContact: async function(groupId, id) {
        try {
            const response = await ApiService.get(`/birthdays/groups/${groupId}/contacts/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch birthday group's contacts
     * 
     * @returns contacts list
     * @throws BirthdayError 
    **/
    fetchContacts: async function(groupId, page) {

       try {
            const response = await ApiService.get(`/birthdays/groups/${groupId}/contacts?page=${page}`)

            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * update birthday group contact
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    updateContact: async function(groupId, id, data) {
        try {
            const response = await ApiService.put(`/birthdays/groups/${groupId}/contacts/${id}`, data)
            
            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete contact by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteContact: async function(groupId, id) {
        try {
            const response = await ApiService.delete(`/birthdays/groups/${groupId}/contacts/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi contacts by ids. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteMultipleContacts: async function(groupId, ids) {
        try {
            const response = await ApiService.delete(`birthdays/groups/${groupId}/contacts`, {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Birthday contact by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    toggleContact: async function(groupId, id) {
        try {
            const response = await ApiService.patch(`/birthdays/groups/${groupId}/contacts/${id}/toggle`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Save birthday message 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    saveMessage: async function(groupId, data) {
        try {
            const response = await ApiService.post(`/birthdays/groups/${groupId}/messages`, data)
            
            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch birthday message by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    fetchMessage: async function(groupId, id) {
        try {
            const response = await ApiService.get(`/birthdays/groups/${groupId}/messages/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Fetch birthday group messages
     * 
     * @returns message list
     * @throws BirthdayError 
    **/
    fetchMessages: async function(groupId, page) {

       try {
            const response = await ApiService.get(`/birthdays/groups/${groupId}/messages?page=${page}`)

            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Update birthday message 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    updateMessage: async function(groupId, id, data) {
        try {
            const response = await ApiService.put(`/birthdays/groups/${groupId}/messages/${id}`, data)
            
            return response.data

        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete message by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteMessage: async function(groupId, id) {
        try {
            const response = await ApiService.delete(`/birthdays/groups/${groupId}/messages/${id}`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Delete multi messages by ids. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    deleteMultipleMessages: async function(groupId, ids) {
        try {
            const response = await ApiService.delete(`birthdays/groups/${groupId}/messages`, {'ids':ids})
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },

    /**
     * Birthday message by id. 
     * 
     * @returns response
     * @throws BirthdayError 
    **/
    toggleMessage: async function(groupId, id) {
        try {
            const response = await ApiService.patch(`/birthdays/groups/${groupId}/messages/${id}/toggle`)
            
            return response.data
        } catch (error) {
            throw new BirthdayError(error.response.status, error.response.data.detail)
        }
    },
}

export { BirthdayService, BirthdayError }
